import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { ActionBarFormButton, ActionBarScheduleButton, WidgetEnv } from "@xapp/stentor-chat-widget";
import { openFormWidgetWhenReady, loadFormWidgetScript, isFormWidgetReady } from "../../utils/formWidgetScript";

import "./FormModal.scss";

export interface FormModalProps {
    /**
     * The form or schedule button configuration that triggered this modal
     */
    readonly config: ActionBarFormButton | ActionBarScheduleButton;
    /**
     * The parent widget environment (used for shared connection settings)
     */
    readonly widgetEnv?: WidgetEnv;
    /**
     * Callback when the modal should close
     */
    readonly onClose: () => void;
}

/**
 * Build the form-widget configuration from ActionBar button config
 */
function buildFormConfig(
    buttonConfig: ActionBarFormButton | ActionBarScheduleButton,
    widgetEnv?: WidgetEnv
): { api: { url: string; key?: string }; intentId?: string; autoGreeting?: string } | null {
    // Option 1: Use dedicated form config if provided
    if (buttonConfig.formConfig) {
        return {
            api: buttonConfig.formConfig.api,
            intentId: buttonConfig.formConfig.intentId,
            autoGreeting: buttonConfig.formConfig.autoGreeting,
        };
    }

    // Option 2: Use shared chat-widget connection settings
    if (widgetEnv?.connection?.serverUrl) {
        return {
            api: {
                url: widgetEnv.connection.serverUrl,
                key: widgetEnv.connection.accountKey,
            },
            intentId: buttonConfig.intentId,
            autoGreeting: buttonConfig.autoGreeting,
        };
    }

    // No valid configuration available
    return null;
}

type LoadingState = "idle" | "loading" | "success" | "error";

const FormModal: FC<FormModalProps> = ({ config, widgetEnv, onClose }) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const formConfig = buildFormConfig(config, widgetEnv);
    const [loadingState, setLoadingState] = useState<LoadingState>("idle");
    const [errorMessage, setErrorMessage] = useState<string>("");

    // Get the form widget key from config or widgetEnv
    const formWidgetKey = config.formConfig?.api?.key || widgetEnv?.formWidgetKey;

    // Attempt to load and open the form widget
    const attemptOpenFormWidget = useCallback(async () => {
        // If form widget is already ready, open it
        if (isFormWidgetReady()) {
            const options = config.preselectChipLabel
                ? { preselectChipLabel: config.preselectChipLabel }
                : undefined;
            const opened = await openFormWidgetWhenReady(options, 5, 100);
            if (opened) {
                setLoadingState("success");
                onClose();
                return;
            }
        }

        // If we have a key, try to load the script
        if (formWidgetKey) {
            setLoadingState("loading");
            try {
                await loadFormWidgetScript(formWidgetKey);
                const options = config.preselectChipLabel
                    ? { preselectChipLabel: config.preselectChipLabel }
                    : undefined;
                const opened = await openFormWidgetWhenReady(options, 30, 100);
                if (opened) {
                    setLoadingState("success");
                    onClose();
                } else {
                    setLoadingState("error");
                    setErrorMessage("Form widget loaded but failed to open. Please try again.");
                }
            } catch (error) {
                setLoadingState("error");
                setErrorMessage("Failed to load form. Please check your connection and try again.");
            }
        } else {
            setLoadingState("error");
            setErrorMessage("Form widget key not configured. Please contact support.");
        }
    }, [config.preselectChipLabel, formWidgetKey, onClose]);

    // Attempt to open form widget on mount
    useEffect(() => {
        attemptOpenFormWidget();
    }, [attemptOpenFormWidget]);

    // Handle Escape key to close
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, []);

    // Focus trap - focus the modal content on mount
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.focus();
        }
    }, []);

    const handleBackdropClick = useCallback(
        (event: React.MouseEvent) => {
            // Only close if clicking the backdrop, not the content
            if (event.target === modalRef.current) {
                onClose();
            }
        },
        [onClose]
    );

    const handleRetry = useCallback(() => {
        setLoadingState("idle");
        setErrorMessage("");
        attemptOpenFormWidget();
    }, [attemptOpenFormWidget]);

    // If form widget opened successfully, the modal will close automatically
    // Show loading/error states in the modal
    if (!formConfig && !formWidgetKey) {
        // No valid configuration available at all
        return (
            <div
                className="xapp-form-modal"
                ref={modalRef}
                onClick={handleBackdropClick}
                role="dialog"
                aria-modal="true"
                aria-label="Form"
            >
                <div
                    className="xapp-form-modal__content"
                    ref={contentRef}
                    tabIndex={-1}
                >
                    <button
                        className="xapp-form-modal__close"
                        onClick={onClose}
                        aria-label="Close form"
                    >
                        &times;
                    </button>
                    <div className="xapp-form-modal__error">
                        <p>Form configuration not available.</p>
                        <p>Please configure the form button with valid settings.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="xapp-form-modal"
            ref={modalRef}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="Form"
        >
            <div
                className="xapp-form-modal__content"
                ref={contentRef}
                tabIndex={-1}
            >
                <button
                    className="xapp-form-modal__close"
                    onClick={onClose}
                    aria-label="Close form"
                >
                    &times;
                </button>
                <div className="xapp-form-modal__form-container">
                    {loadingState === "loading" && (
                        <div className="xapp-form-modal__loading">
                            <div className="xapp-form-modal__spinner" aria-hidden="true" />
                            <p>Loading form...</p>
                        </div>
                    )}
                    {loadingState === "error" && (
                        <div className="xapp-form-modal__error">
                            <p>{errorMessage}</p>
                            <button
                                className="xapp-form-modal__retry"
                                onClick={handleRetry}
                            >
                                Try Again
                            </button>
                        </div>
                    )}
                    {loadingState === "idle" && (
                        <div className="xapp-form-modal__loading">
                            <div className="xapp-form-modal__spinner" aria-hidden="true" />
                            <p>Initializing...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FormModal;
